import { haversineMeters } from './densifyRoutePoints'
import { localDateTimeInputToUtcIso } from './dateTimeLocal'

const DEFAULT_PROFILE = 'mapbox/driving-traffic'
const DEFAULT_RADIUS_M = 25
/** Slightly looser snap when timed matching is used (depart_at / timestamps). */
const TIMED_RADIUS_M = 35

/**
 * Simple concurrency limiter.
 * @param {number} concurrency
 */
export function createQueue(concurrency = 2) {
  let active = 0
  const waiting = []

  const runNext = () => {
    if (active >= concurrency || waiting.length === 0) {
      return
    }
    const { fn, resolve, reject } = waiting.shift()
    active += 1
    Promise.resolve()
      .then(fn)
      .then(
        (value) => {
          active -= 1
          resolve(value)
          runNext()
        },
        (err) => {
          active -= 1
          reject(err)
          runNext()
        }
      )
  }

  return function enqueue(fn) {
    return new Promise((resolve, reject) => {
      waiting.push({ fn, resolve, reject })
      runNext()
    })
  }
}

/**
 * Parse departure instant to epoch ms.
 * Prefers `departAt` ISO-8601 (Zulu recommended), else UI `dateTime`
 * (datetime-local value interpreted as local wall time → UTC).
 *
 * @param {{ departAt?: string|null, dateTime?: { type?: number, value?: string } }} options
 * @returns {number|null} epoch ms, or null to omit timestamps
 */
export function resolveDepartureMs({ departAt = null, dateTime = null } = {}) {
  if (typeof departAt === 'string' && departAt.trim()) {
    const parsed = Date.parse(departAt.trim())
    if (!Number.isNaN(parsed)) {
      return parsed
    }
  }

  if (!dateTime || dateTime.type == null || dateTime.type < 0) {
    return null
  }
  if (dateTime.type === 0) {
    return Date.now()
  }
  if (dateTime.type === 1 || dateTime.type === 2) {
    if (!dateTime.value) {
      return Date.now()
    }
    const utcIso = localDateTimeInputToUtcIso(dateTime.value)
    if (utcIso) {
      return Date.parse(utcIso)
    }
    const parsed = Date.parse(dateTime.value)
    return Number.isNaN(parsed) ? Date.now() : parsed
  }
  return null
}

/**
 * Normalize departure to ISO-8601 (always with ms + Z when from Date).
 * Prefers explicit `departAt`; else derives from UI `dateTime`.
 *
 * @param {{ departAt?: string|null, dateTime?: { type?: number, value?: string } }} options
 * @returns {string|null}
 */
export function resolveDepartAtIso(options = {}) {
  const { departAt = null, dateTime = null } = options
  if (typeof departAt === 'string' && departAt.trim()) {
    const parsed = Date.parse(departAt.trim())
    if (!Number.isNaN(parsed)) {
      return new Date(parsed).toISOString()
    }
  }
  const ms = resolveDepartureMs({ departAt: null, dateTime })
  if (ms == null) {
    return null
  }
  return new Date(ms).toISOString()
}

/**
 * Window where Mapbox live `congestion` annotations are trusted on the map.
 * Outside this (future depart / arrive-by), color from predicted duration vs free-flow.
 */
export const NEAR_NOW_WINDOW_MS = 30 * 60 * 1000

/**
 * True when the request is leave-now or leave within ±NEAR_NOW_WINDOW_MS of now.
 * Arrive-by is never treated as live congestion.
 *
 * @param {{ departAt?: string|null, dateTime?: { type?: number, value?: string }, nowMs?: number }} options
 */
export function isNearNowDeparture({
  departAt = null,
  dateTime = null,
  nowMs = Date.now(),
} = {}) {
  if (dateTime && dateTime.type === 2) {
    return false
  }
  if (dateTime && dateTime.type === 0) {
    return true
  }
  // No timed departure → Map Matching defaults to present → live congestion
  if (
    !(typeof departAt === 'string' && departAt.trim()) &&
    (!dateTime || dateTime.type == null || dateTime.type < 0)
  ) {
    return true
  }
  const ms = resolveDepartureMs({ departAt, dateTime })
  if (ms == null) {
    return true
  }
  return Math.abs(ms - nowMs) <= NEAR_NOW_WINDOW_MS
}

/**
 * Format for Mapbox Matching `depart_at`:
 * `YYYY-MM-DDThh:mm:ssZ` (no milliseconds).
 *
 * @param {string|number|null|undefined} isoOrMs
 * @returns {string|null}
 */
export function formatMapboxDepartAt(isoOrMs) {
  if (isoOrMs == null || isoOrMs === '') {
    return null
  }
  const ms =
    typeof isoOrMs === 'number' ? isoOrMs : Date.parse(String(isoOrMs).trim())
  if (Number.isNaN(ms)) {
    return null
  }
  return new Date(ms).toISOString().replace(/\.\d{3}Z$/, 'Z')
}

/**
 * True when the caller asked to leave at a specific time (not "omit" / arrive-by only).
 * - explicit `departAt` ISO string
 * - UI `dateTime.type === 1` (depart at)
 * - UI `dateTime.type === 0` (leave now) — still useful so Mapbox gets an explicit instant
 *
 * @param {{ departAt?: string|null, dateTime?: { type?: number, value?: string } }} options
 */
export function shouldSendDepartAt({ departAt = null, dateTime = null } = {}) {
  if (typeof departAt === 'string' && departAt.trim()) {
    return !Number.isNaN(Date.parse(departAt.trim()))
  }
  if (!dateTime || dateTime.type == null) {
    return false
  }
  return dateTime.type === 0 || dateTime.type === 1
}

/**
 * Build ascending unix timestamps for Map Matching from departure.
 *
 * Supports:
 * - options object: `{ coordinates, departAt, dateTime, legDurationSec }`
 * - legacy: `(coordCount, dateTime, legDurationSec)`
 *
 * When coordinates are provided, time is distributed by **path distance**
 * (more realistic than equal steps), scaled to leg duration, then forced
 * strictly ascending (Mapbox requirement).
 *
 * @returns {number[]|null} unix seconds
 */
export function buildTimestamps(arg1, arg2, arg3) {
  let coordinates = null
  let coordCount = 0
  let departAt = null
  let dateTime = null
  let legDurationSec = 0

  if (arg1 && typeof arg1 === 'object' && !Array.isArray(arg1)) {
    coordinates = arg1.coordinates || null
    coordCount =
      arg1.coordinateCount ||
      (Array.isArray(coordinates) ? coordinates.length : 0)
    departAt = arg1.departAt ?? null
    dateTime = arg1.dateTime ?? null
    legDurationSec = arg1.legDurationSec || 0
  } else {
    coordCount = arg1
    dateTime = arg2
    legDurationSec = arg3 || 0
  }

  if (coordCount < 2) {
    return null
  }

  const startMs = resolveDepartureMs({ departAt, dateTime })
  if (startMs == null) {
    return null
  }

  const startSec = Math.floor(startMs / 1000)
  const durationSec = Math.max(legDurationSec || 0, coordCount - 1)

  // Distance-weighted progression along densify points when available
  let fractions = null
  if (Array.isArray(coordinates) && coordinates.length === coordCount) {
    const cum = [0]
    for (let i = 0; i < coordinates.length - 1; i++) {
      const [lon0, lat0] = coordinates[i]
      const [lon1, lat1] = coordinates[i + 1]
      cum.push(
        cum[i] +
          haversineMeters({ lon: lon0, lat: lat0 }, { lon: lon1, lat: lat1 })
      )
    }
    const total = cum[cum.length - 1]
    if (total > 0) {
      fractions = cum.map((value) => value / total)
    }
  }

  const stamps = []
  for (let i = 0; i < coordCount; i++) {
    const frac = fractions ? fractions[i] : i / (coordCount - 1)
    let stamp = Math.floor(startSec + durationSec * frac)
    if (i > 0 && stamp <= stamps[i - 1]) {
      stamp = stamps[i - 1] + 1
    }
    stamps.push(stamp)
  }
  return stamps
}

/**
 * Flatten one matching's legs into annotations + totals.
 */
function flattenMatching(matching) {
  const matchingLegs = Array.isArray(matching?.legs) ? matching.legs : []
  const annotations = {
    duration: [],
    distance: [],
    congestion: [],
    speed: [],
    maxspeed: [],
  }
  for (const matchingLeg of matchingLegs) {
    const ann = matchingLeg.annotation || {}
    if (Array.isArray(ann.duration)) {
      annotations.duration.push(...ann.duration)
    }
    if (Array.isArray(ann.distance)) {
      annotations.distance.push(...ann.distance)
    }
    if (Array.isArray(ann.congestion)) {
      annotations.congestion.push(...ann.congestion)
    }
    if (Array.isArray(ann.speed)) {
      annotations.speed.push(...ann.speed)
    }
    if (Array.isArray(ann.maxspeed)) {
      annotations.maxspeed.push(...ann.maxspeed)
    }
  }

  const durationFromLegs = matchingLegs.reduce(
    (sum, matchingLeg) => sum + (matchingLeg.duration || 0),
    0
  )
  const distanceFromLegs = matchingLegs.reduce(
    (sum, matchingLeg) => sum + (matchingLeg.distance || 0),
    0
  )

  const coords = matching?.geometry?.coordinates
  return {
    geometry:
      matching?.geometry ||
      (Array.isArray(coords)
        ? { type: 'LineString', coordinates: coords }
        : null),
    duration: matching?.duration ?? durationFromLegs ?? 0,
    distance: matching?.distance ?? distanceFromLegs ?? 0,
    confidence:
      typeof matching?.confidence === 'number' ? matching.confidence : null,
    annotations,
    matching,
  }
}

function appendGeometry(targetCoords, nextCoords) {
  if (!Array.isArray(nextCoords) || nextCoords.length === 0) {
    return
  }
  if (targetCoords.length === 0) {
    targetCoords.push(...nextCoords)
    return
  }
  const last = targetCoords[targetCoords.length - 1]
  const first = nextCoords[0]
  const same =
    last &&
    first &&
    Math.abs(last[0] - first[0]) < 1e-9 &&
    Math.abs(last[1] - first[1]) < 1e-9
  targetCoords.push(...(same ? nextCoords.slice(1) : nextCoords))
}

function matchingOrderFromTracepoints(matchings, tracepoints) {
  const order = []
  const seen = new Set()
  let unmatchedTracepointCount = 0

  if (Array.isArray(tracepoints) && tracepoints.length) {
    for (const point of tracepoints) {
      if (!point) {
        unmatchedTracepointCount += 1
        continue
      }
      const idx = point.matchings_index
      if (typeof idx === 'number' && idx >= 0 && idx < matchings.length) {
        if (!seen.has(idx)) {
          seen.add(idx)
          order.push(idx)
        }
      }
    }
  }

  if (!order.length) {
    for (let i = 0; i < matchings.length; i++) {
      order.push(i)
    }
  } else {
    // Include any matchings never referenced (append at end)
    for (let i = 0; i < matchings.length; i++) {
      if (!seen.has(i)) {
        order.push(i)
      }
    }
  }

  return { order, unmatchedTracepointCount }
}

/**
 * Stitch Mapbox sub-matchings into one geometry + merged annotations.
 * Multiple matchings are sub-matches of a broken/outlier trace — not alternatives.
 *
 * @param {object[]} matchings
 * @param {Array<object|null>} [tracepoints]
 */
export function stitchMatchings(matchings, tracepoints = []) {
  if (!Array.isArray(matchings) || matchings.length === 0) {
    return null
  }

  if (matchings.length === 1) {
    const single = flattenMatching(matchings[0])
    return {
      ...single,
      matchingCount: 1,
      unmatchedTracepointCount: Array.isArray(tracepoints)
        ? tracepoints.filter((point) => point == null).length
        : 0,
      stitched: false,
    }
  }

  const { order, unmatchedTracepointCount } = matchingOrderFromTracepoints(
    matchings,
    tracepoints
  )

  const coordinates = []
  const annotations = {
    duration: [],
    distance: [],
    congestion: [],
    speed: [],
    maxspeed: [],
  }
  let duration = 0
  let distance = 0
  let confidenceWeight = 0
  let confidenceSum = 0

  for (const idx of order) {
    const flat = flattenMatching(matchings[idx])
    appendGeometry(coordinates, flat.geometry?.coordinates)
    for (const key of Object.keys(annotations)) {
      if (Array.isArray(flat.annotations[key])) {
        annotations[key].push(...flat.annotations[key])
      }
    }
    duration += flat.duration || 0
    distance += flat.distance || 0
    if (typeof flat.confidence === 'number') {
      const weight = flat.distance > 0 ? flat.distance : 1
      confidenceSum += flat.confidence * weight
      confidenceWeight += weight
    }
  }

  return {
    geometry:
      coordinates.length >= 2 ? { type: 'LineString', coordinates } : null,
    duration,
    distance,
    confidence: confidenceWeight > 0 ? confidenceSum / confidenceWeight : null,
    annotations,
    matching: matchings[order[0]],
    matchingCount: matchings.length,
    unmatchedTracepointCount,
    stitched: true,
    matchingOrder: order,
  }
}

/**
 * @param {object} params
 * @param {string} params.accessToken
 * @param {number[][]} params.coordinates [lon,lat][]
 * @param {string} [params.profile]
 * @param {number[]} [params.timestamps]
 * @param {string|null} [params.departAt] ISO-8601 departure → Mapbox `depart_at`
 * @param {number} [params.radius]
 * @param {typeof fetch} [params.fetchImpl]
 */
export async function postMapMatching({
  accessToken,
  coordinates,
  profile = DEFAULT_PROFILE,
  timestamps = null,
  departAt = null,
  radius = null,
  fetchImpl = fetch,
}) {
  if (!accessToken) {
    return {
      ok: false,
      code: 'missing_token',
      message: 'Mapbox access token is missing',
      status: 0,
      raw: null,
    }
  }
  if (!Array.isArray(coordinates) || coordinates.length < 2) {
    return {
      ok: false,
      code: 'too_few_coordinates',
      message: 'Need at least 2 coordinates for map matching',
      status: 0,
      raw: null,
    }
  }
  if (coordinates.length > 100) {
    return {
      ok: false,
      code: 'too_many_coordinates',
      message: `Too many coordinates (${coordinates.length}); max 100`,
      status: 0,
      raw: null,
      coordinateCount: coordinates.length,
    }
  }

  const mapboxDepartAt = formatMapboxDepartAt(departAt)
  const hasTimestamps =
    Array.isArray(timestamps) && timestamps.length === coordinates.length
  const effectiveRadius =
    radius != null
      ? radius
      : hasTimestamps || mapboxDepartAt
      ? TIMED_RADIUS_M
      : DEFAULT_RADIUS_M

  const coordStr = coordinates.map(([lon, lat]) => `${lon},${lat}`).join(';')
  const radiuses = coordinates.map(() => String(effectiveRadius)).join(';')
  // Only endpoints are waypoints so densify points guide as silent vias.
  const lastIdx = coordinates.length - 1
  const waypoints = `0;${lastIdx}`

  const body = new URLSearchParams()
  body.set('coordinates', coordStr)
  body.set('radiuses', radiuses)
  body.set('waypoints', waypoints)
  body.set('geometries', 'geojson')
  body.set('overview', 'full')
  body.set('steps', 'true')
  body.set('annotations', 'duration,distance,congestion,speed,maxspeed')
  if (mapboxDepartAt) {
    // Traffic conditions at departure (Map Matching driving-traffic)
    body.set('depart_at', mapboxDepartAt)
  }
  if (hasTimestamps) {
    body.set('timestamps', timestamps.join(';'))
  }

  const url = `https://api.mapbox.com/matching/v5/${profile}?access_token=${encodeURIComponent(
    accessToken
  )}`

  let res
  try {
    res = await fetchImpl(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: body.toString(),
    })
  } catch (error) {
    return {
      ok: false,
      code: 'network_error',
      message: error?.message || 'Network error calling Mapbox',
      status: 0,
      raw: null,
      networkError: error,
    }
  }

  let json = null
  try {
    json = await res.json()
  } catch {
    json = null
  }

  if (res.status === 429) {
    return {
      ok: false,
      code: 'rate_limited',
      message: 'Mapbox rate limit (300 rpm); try again shortly',
      status: 429,
      raw: json,
    }
  }

  if (!res.ok) {
    return {
      ok: false,
      code: 'http_error',
      message: json?.message || json?.error || `Mapbox HTTP ${res.status}`,
      status: res.status,
      raw: json,
    }
  }

  if (
    !json ||
    json.code !== 'Ok' ||
    !Array.isArray(json.matchings) ||
    !json.matchings.length
  ) {
    return {
      ok: false,
      code: json?.code || 'no_match',
      message: json?.message || 'No Mapbox matchings returned',
      status: res.status,
      raw: json,
    }
  }

  const stitched = stitchMatchings(json.matchings, json.tracepoints || [])
  if (!stitched || !stitched.geometry) {
    return {
      ok: false,
      code: 'stitch_failed',
      message: 'Could not stitch Mapbox matchings',
      status: res.status,
      raw: json,
    }
  }

  return {
    ok: true,
    code: 'Ok',
    status: res.status,
    matching: stitched.matching,
    geometry: stitched.geometry,
    duration: stitched.duration,
    distance: stitched.distance,
    confidence: stitched.confidence,
    annotations: stitched.annotations,
    matchingCount: stitched.matchingCount,
    unmatchedTracepointCount: stitched.unmatchedTracepointCount,
    stitched: stitched.stitched,
    matchingOrder: stitched.matchingOrder || [0],
    departAt: mapboxDepartAt,
    uuid: json.uuid || null,
    raw: json,
  }
}
